import React, { useState, useEffect } from 'react';
import { 
  Heart, Users, Zap, MapPin, Calendar, Target, TrendingUp,
  ChevronRight, Share2, Clock, CheckCircle, Sun, Home,
  ArrowRight, Gift, Shield, Eye, Loader2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const API = process.env.REACT_APP_BACKEND_URL;

const AccionSocialPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [donationAmount, setDonationAmount] = useState('');
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [recentDonations, setRecentDonations] = useState([]);
  const [globalStats, setGlobalStats] = useState({
    total_raised: 0,
    total_donors: 0,
    total_beneficiaries: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cargar datos del API
  useEffect(() => {
    const loadData = async () => {
      try {
        const [projectsRes, statsRes, donationsRes] = await Promise.all([
          axios.get(`${API}/api/social-projects/public`),
          axios.get(`${API}/api/social-projects/public/stats`),
          axios.get(`${API}/api/social-projects/public/donations/recent?limit=10`)
        ]);
        
        setProjects(projectsRes.data);
        setGlobalStats(statsRes.data);
        setRecentDonations(donationsRes.data);
      } catch (error) {
        console.error('Error cargando datos:', error);
        // Si no hay proyectos en la BD, mostrar los de ejemplo
        setProjects(defaultProjects);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Proyectos de ejemplo (se usarán solo si la BD está vacía)
  const defaultProjects = [
    {
      id: 'ejemplo-1',
      title: 'Luz para Escuela Rural de Concepción',
      location: 'Loreto, Concepción',
      description: 'La Escuela Básica N° 1847 "San Francisco de Asís" no tiene acceso a electricidad confiable. Los 47 niños estudian con velas cuando hay cortes de luz, lo cual ocurre casi todos los días. Queremos instalar un sistema solar de 3kW con baterías para que los niños puedan estudiar con luz y usar computadoras.',
      short_description: 'Sistema solar de 3kW para escuela con 47 niños que estudian con velas por los constantes cortes de luz.',
      image_url: 'https://images.unsplash.com/photo-1497486751825-1233686d5d80?w=800',
      goal_amount: 45000000,
      raised_amount: 0,
      donors_count: 0,
      category: 'Educación',
      status: 'active',
      beneficiaries: 47,
      system_size: '3 kW',
      impact: 'Luz para 47 niños y 5 profesores, acceso a computadoras y proyector',
      featured: true
    },
    {
      id: 'ejemplo-2',
      title: 'Energía para Centro de Salud Comunitario',
      location: 'Ñeembucú, Pilar',
      description: 'El puesto de salud de la comunidad de Isla Umbú atiende a más de 200 familias pero sufre cortes de luz constantes. Las vacunas se echan a perder, no pueden usar equipos médicos básicos y las emergencias nocturnas son atendidas con linternas.',
      short_description: 'Sistema solar con baterías para centro de salud que atiende a 200 familias sin electricidad confiable.',
      image_url: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800',
      goal_amount: 65000000,
      raised_amount: 0,
      donors_count: 0,
      category: 'Salud',
      status: 'active',
      beneficiaries: 200,
      system_size: '5 kW',
      impact: 'Refrigeración de vacunas 24/7, iluminación para emergencias nocturnas',
      featured: false
    },
    {
      id: 'ejemplo-3',
      title: 'Iluminación para Comunidad Indígena Mbya',
      location: 'Caaguazú',
      description: 'La comunidad Mbya Guaraní "Ka\'aguy Poty" de 15 familias vive completamente sin acceso a electricidad. Los niños no pueden hacer tareas de noche, y las familias dependen de velas y mecheros que representan un riesgo de incendio.',
      short_description: 'Sistema solar comunitario para 15 familias indígenas sin ningún acceso a electricidad.',
      image_url: 'https://images.unsplash.com/photo-1509099836639-18ba1795216d?w=800',
      goal_amount: 38000000,
      raised_amount: 0,
      donors_count: 0,
      category: 'Comunidad',
      status: 'active',
      beneficiaries: 15,
      system_size: '2 kW + luces individuales',
      impact: 'Luz para 15 familias, carga de celulares, seguridad nocturna',
      featured: false
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PY', {
      style: 'decimal',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const calculateProgress = (raised, goal) => {
    return Math.min((raised / goal) * 100, 100);
  };

  const predefinedAmounts = [50000, 100000, 250000, 500000, 1000000];

  const handleDonate = (project) => {
    setSelectedProject(project);
    setShowDonationModal(true);
  };

  const handleShare = (project) => {
    if (navigator.share) {
      navigator.share({
        title: `Ayudá a: ${project.title}`,
        text: project.short_description,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('¡Link copiado al portapapeles!');
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours} horas`;
    return `Hace ${diffDays} días`;
  };

  const displayProjects = projects.length > 0 ? projects : defaultProjects;
  const totalBeneficiaries = displayProjects.reduce((sum, p) => sum + (p.beneficiaries || 0), 0);

  return (
    <div className="min-h-screen bg-zinc-50">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900 via-emerald-800 to-zinc-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-400 rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-400 rounded-full filter blur-[120px]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6">
              <Heart className="w-4 h-4 text-red-400" />
              <span className="text-sm font-medium text-white">Acción Social</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Llevamos Luz a Quienes
              <span className="text-yellow-400"> Más lo Necesitan</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-emerald-100 mb-8 leading-relaxed">
              Hay comunidades en Paraguay que viven sin electricidad o con cortes constantes. 
              Con tu donación, instalamos sistemas solares que cambian vidas. 
              <strong className="text-white">100% transparente, cada guaraní cuenta.</strong>
            </p>

            <div className="flex flex-wrap gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3">
                <p className="text-2xl font-bold text-white">
                  {loading ? '...' : `Gs. ${formatCurrency(globalStats.total_raised)}`}
                </p>
                <p className="text-sm text-emerald-200">Total recaudado</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3">
                <p className="text-2xl font-bold text-white">
                  {loading ? '...' : globalStats.total_donors}
                </p>
                <p className="text-sm text-emerald-200">Donantes</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3">
                <p className="text-2xl font-bold text-white">
                  {loading ? '...' : displayProjects.length}
                </p>
                <p className="text-sm text-emerald-200">Proyectos activos</p>
              </div>
            </div>
            
            <a
              href="#proyectos"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-yellow-400 hover:bg-yellow-300 text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:scale-105"
            >
              Ver Proyectos Activos
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-12 bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-8">
            <h2 className="text-xl font-bold text-zinc-900">¿Cómo funciona?</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Eye, title: 'Elegí un proyecto', desc: 'Conocé la comunidad y su necesidad' },
              { icon: Gift, title: 'Doná cualquier monto', desc: 'Desde Gs. 10.000 ayuda' },
              { icon: Target, title: 'Alcanzamos la meta', desc: 'Todo es 100% transparente' },
              { icon: Sun, title: 'Instalamos el sistema', desc: 'Luz para quienes lo necesitan' }
            ].map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="font-bold text-zinc-900 mb-1">{step.title}</h3>
                  <p className="text-sm text-zinc-500">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section id="proyectos" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-2">
                Proyectos Activos
              </h2>
              <p className="text-zinc-600">Elegí un proyecto y ayudá a cambiar vidas</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
              {displayProjects.filter(p => p.status === 'active').length} proyectos activos
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayProjects.map((project) => (
                <div 
                  key={project.id} 
                  className="bg-white rounded-2xl overflow-hidden border border-zinc-200 transition-all duration-300 hover:shadow-xl hover:border-emerald-300 group"
                >
                  {/* Image - Clickable */}
                  <Link to={`/proyectos/accion-social/${project.id}`} className="block relative h-48 overflow-hidden">
                    <img 
                      src={project.image_url} 
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-zinc-700">
                        {project.category}
                      </span>
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 px-4 py-2 rounded-full text-sm font-medium text-zinc-800">
                        Ver detalles
                      </span>
                    </div>
                  </Link>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-sm text-zinc-500 mb-2">
                      <MapPin className="w-4 h-4" />
                      {project.location}
                    </div>
                    
                    <Link to={`/proyectos/accion-social/${project.id}`}>
                      <h3 className="text-lg font-bold text-zinc-900 mb-2 line-clamp-2 hover:text-emerald-600 transition-colors">
                        {project.title}
                      </h3>
                    </Link>
                    
                    <p className="text-sm text-zinc-600 mb-4 line-clamp-2">
                      {project.short_description}
                    </p>

                    {/* Progress bar */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="font-bold text-emerald-600">
                          Gs. {formatCurrency(project.raised_amount)}
                        </span>
                        <span className="text-zinc-500">
                          de Gs. {formatCurrency(project.goal_amount)}
                        </span>
                      </div>
                      <div className="h-3 bg-zinc-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500"
                          style={{ width: `${calculateProgress(project.raised_amount, project.goal_amount)}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs text-zinc-500 mt-2">
                        <span>{Math.round(calculateProgress(project.raised_amount, project.goal_amount))}% completado</span>
                        <span>{project.donors_count} donantes</span>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center justify-between py-3 border-t border-zinc-100 mb-4">
                      <div className="flex items-center gap-1 text-sm text-zinc-600">
                        <Users className="w-4 h-4" />
                        <span>{project.beneficiaries} beneficiarios</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-emerald-600">
                        <Zap className="w-4 h-4" />
                        <span>{project.system_size}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleDonate(project)}
                        className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
                      >
                        <Heart className="w-4 h-4" />
                        Donar
                      </button>
                      <button
                        onClick={() => handleShare(project)}
                        className="px-4 py-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-xl transition-colors"
                      >
                        <Share2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Transparency Section */}
      <section className="py-16 md:py-24 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 rounded-full mb-6">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span className="text-sm font-medium text-emerald-400">100% Transparente</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
                Cada Guaraní Cuenta y se Rinde
              </h2>
              
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Creemos en la transparencia total. Por eso, publicamos cada donación recibida y 
                mostramos exactamente cómo se usa el dinero. Cuando alcanzamos la meta, 
                compartimos fotos y videos de la instalación.
              </p>
              
              <div className="space-y-4">
                {[
                  'Todas las donaciones se publican (sin datos personales)',
                  'Desglose de costos de cada instalación',
                  'Fotos y videos del antes y después',
                  'Testimonios de los beneficiarios',
                  'Informes de impacto mensuales'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-zinc-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                Últimas Donaciones
              </h3>
              
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {recentDonations.length > 0 ? (
                  recentDonations.map((donation, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center">
                          <Heart className="w-4 h-4 text-emerald-400" />
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{donation.donor_name}</p>
                          <p className="text-zinc-500 text-xs">{donation.project_title}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-400 font-bold text-sm">Gs. {formatCurrency(donation.amount)}</p>
                        <p className="text-zinc-500 text-xs">{formatTimeAgo(donation.created_at)}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Heart className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500">Aún no hay donaciones</p>
                    <p className="text-zinc-600 text-sm">¡Sé el primero en ayudar!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-yellow-400 to-yellow-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12 text-center">
          <Heart className="w-12 h-12 text-yellow-800 mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
            ¿Querés Proponer una Comunidad?
          </h2>
          <p className="text-lg text-zinc-700 mb-8 max-w-2xl mx-auto">
            Si conocés una comunidad que necesita energía solar, contanos. 
            Evaluamos cada caso y creamos un proyecto para ayudarlos.
          </p>
          <a
            href="https://wa.me/595991344956?text=Hola%20Zenex!%20Quiero%20proponer%20una%20comunidad%20para%20el%20programa%20de%20Acción%20Social"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 bg-zinc-900 text-white font-bold rounded-xl hover:bg-zinc-800 transition-colors inline-flex items-center gap-2"
          >
            Proponer Comunidad
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Donation Modal */}
      {showDonationModal && selectedProject && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-zinc-900">Hacer una Donación</h3>
                <button 
                  onClick={() => setShowDonationModal(false)}
                  className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
                >
                  <span className="text-2xl text-zinc-500">&times;</span>
                </button>
              </div>
              
              <div className="bg-zinc-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-zinc-600 mb-1">Donando a:</p>
                <p className="font-bold text-zinc-900">{selectedProject.title}</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-zinc-700 mb-3">
                  Seleccioná un monto o ingresá el tuyo
                </label>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {predefinedAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setDonationAmount(amount.toString())}
                      className={`py-3 rounded-xl text-sm font-medium transition-colors ${
                        donationAmount === amount.toString()
                          ? 'bg-emerald-500 text-white'
                          : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                      }`}
                    >
                      Gs. {formatCurrency(amount)}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500">Gs.</span>
                  <input
                    type="number"
                    value={donationAmount}
                    onChange={(e) => setDonationAmount(e.target.value)}
                    placeholder="Otro monto"
                    className="w-full pl-12 pr-4 py-3 border border-zinc-300 rounded-xl focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-emerald-800">
                  <strong>💚 Tu donación ayudará a:</strong> {selectedProject.beneficiaries} personas 
                  de {selectedProject.location} a tener acceso a energía limpia.
                </p>
              </div>

              <a
                href={`https://wa.me/595991344956?text=Hola%20Zenex!%20Quiero%20donar%20Gs.%20${formatCurrency(parseInt(donationAmount) || 0)}%20al%20proyecto:%20${encodeURIComponent(selectedProject.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <Heart className="w-5 h-5" />
                Continuar con la Donación
              </a>
              
              <p className="text-xs text-zinc-500 text-center mt-4">
                Te contactaremos por WhatsApp para coordinar el pago de forma segura.
              </p>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default AccionSocialPage;
